import { useState, useEffect } from 'react';
import { apiFetch } from '../../stores/authStore';
import { useExportActions } from './useExportActions';
import { fetchCsvExport } from './fetchCsvExport';
import type { OrgMember } from './memberTypes';
import type { MemberExportSlots } from './MembersPanel';
import type { GoogleProductionPreview } from './exportTypes';

/**
 * Every Manager export workflow in one hook (#343): the Fill/accounting,
 * Settlements and Order audit CSV downloads, both Google Sheets tools, and the
 * per-member export slots the governance cards render.
 *
 * It depends on governance in exactly one place, passed in: the member list it
 * offers as export subjects. Every error is panel-local. Governance imports
 * nothing from here — the direction #342 established.
 */

const DEFAULT_GOOGLE_LEDGER_SPREADSHEET_ID = '1rANLkhICCyHf-PU3hA7xGde5M1-5dbrgmr-XZcy6KMs';
const DEFAULT_GOOGLE_PRODUCTION_LEDGER_SPREADSHEET_ID = '1Mh3gAhPrquklvXJV2pY7hg0AwzpEHjAprKr4h_uIPzo';
const DEFAULT_GOOGLE_PRODUCTION_TARGET_SHEET_TITLE = 'G1 Import - Moving';
const DEFAULT_GOOGLE_PRODUCTION_SOURCE_SHEET_TITLE = 'Moving';
const DEFAULT_GOOGLE_PRODUCTION_FROM_DATE = '2026-05-01';

function buildExportUrl(
  endpoint: string,
  orgQueryParam: string,
  options: {
    fromDate: string;
    toDate: string;
    traderIds?: string[];
  },
): string {
  const params = new URLSearchParams(orgQueryParam ? orgQueryParam.slice(1) : '');
  if (options.fromDate) params.set('from', options.fromDate);
  if (options.toDate) params.set('to', options.toDate);
  for (const traderId of options.traderIds ?? []) {
    params.append('traderId', traderId);
  }
  const query = params.toString();
  return query ? `${endpoint}?${query}` : endpoint;
}

function dateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function exportPresetRange(days: number | null): { fromDate: string; toDate: string } {
  if (days == null) return { fromDate: '', toDate: '' };
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - days);
  return {
    fromDate: dateInputValue(from),
    toDate: dateInputValue(to),
  };
}

function defaultGoogleTestSheetTitle(scope: 'Prof Trades' | 'Org Trades' = 'Prof Trades'): string {
  const now = new Date();
  const stamp = now.toISOString().slice(5, 19).replace('T', ' ').replace(/[-:]/g, '');
  return `G1 Test ${stamp} ${scope}`;
}

function isDefaultGoogleTestSheetTitle(title: string): boolean {
  return /^G1 Test \d{4} \d{6} (Prof|Org) Trades$/.test(title.trim());
}

/** #461 — a Google trader choice STAMPED with the effective org it was made
 *  under. The rendered/requested id is derived by matching the stamp against
 *  the current scope key (same derive-don't-store shape as #349's org
 *  stamps), so an org switch blanks both Google panels on its first render —
 *  there is no window where the old org's trader can ride a new org's
 *  request. */
interface ScopedGoogleTraderSelection {
  scopeKey: string;
  traderId: string;
}

const EMPTY_GOOGLE_ACCOUNTS: OrgMember[] = [];

export function useExportWorkflows({
  orgQueryParam,
  members,
  googleTraderScopeKey,
  googleTraderScopeReady,
}: {
  orgQueryParam: string;
  members: OrgMember[];
  googleTraderScopeKey: string;
  googleTraderScopeReady: boolean;
}) {
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [selectedExportTraderIds, setSelectedExportTraderIds] = useState<string[]>([]);
  // Per-export pending/done/error state (#126): the org export and each trader
  // run independently, each with its own status, so one failure never strands
  // or clobbers another.
  const exports = useExportActions();
  const [googleSpreadsheetId, setGoogleSpreadsheetId] = useState(DEFAULT_GOOGLE_LEDGER_SPREADSHEET_ID);
  const [googleTargetSheetTitle, setGoogleTargetSheetTitle] = useState(defaultGoogleTestSheetTitle);
  // Two independent stamped selections — choosing a trader in one Google
  // panel must not move the other. See ScopedGoogleTraderSelection above.
  const [googleTraderSelection, setGoogleTraderSelection] =
    useState<ScopedGoogleTraderSelection>(() => ({
      scopeKey: googleTraderScopeKey,
      traderId: '',
    }));
  const [googleAllTraders, setGoogleAllTraders] = useState(false);
  const [googleFromDate, setGoogleFromDate] = useState('');
  const [googleToDate, setGoogleToDate] = useState('');
  const [googleAccessToken, setGoogleAccessToken] = useState('');
  const [googleSourceLabel, setGoogleSourceLabel] = useState('NBAProf');
  const [googleBettorLabel, setGoogleBettorLabel] = useState('The Professor');
  const [googleBookLabel, setGoogleBookLabel] = useState('dash');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleMessage, setGoogleMessage] = useState<string | null>(null);
  // Panel-local, like googleProdError below. Until #343 this handler wrote the
  // shared governance error instead, which renders only inside MembersPanel's
  // settings block — so a failed test load on the Exports tab showed nothing.
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [googleProdSpreadsheetId, setGoogleProdSpreadsheetId] = useState(DEFAULT_GOOGLE_PRODUCTION_LEDGER_SPREADSHEET_ID);
  const [googleProdSourceSheetTitle, setGoogleProdSourceSheetTitle] = useState(DEFAULT_GOOGLE_PRODUCTION_SOURCE_SHEET_TITLE);
  const [googleProdTargetSheetTitle, setGoogleProdTargetSheetTitle] = useState(DEFAULT_GOOGLE_PRODUCTION_TARGET_SHEET_TITLE);
  const [googleProdTraderSelection, setGoogleProdTraderSelection] =
    useState<ScopedGoogleTraderSelection>(() => ({
      scopeKey: googleTraderScopeKey,
      traderId: '',
    }));
  const [googleProdFromDate, setGoogleProdFromDate] = useState(DEFAULT_GOOGLE_PRODUCTION_FROM_DATE);
  const [googleProdToDate, setGoogleProdToDate] = useState('');
  const [googleProdVenue, setGoogleProdVenue] = useState<'kalshi' | 'polymarket' | 'all'>('kalshi');
  const [googleProdAllowPolymarket, setGoogleProdAllowPolymarket] = useState(false);
  const [googleProdAccessToken, setGoogleProdAccessToken] = useState('');
  const [googleProdSourceLabel, setGoogleProdSourceLabel] = useState('Prof');
  const [googleProdBettorLabel, setGoogleProdBettorLabel] = useState('The Professor');
  const [googleProdBookLabel, setGoogleProdBookLabel] = useState('dash');
  const [googleProdPreview, setGoogleProdPreview] = useState<GoogleProductionPreview | null>(null);
  const [googleProdConfirmation, setGoogleProdConfirmation] = useState('');
  const [googleProdLoading, setGoogleProdLoading] = useState<'preview' | 'apply' | null>(null);
  const [googleProdMessage, setGoogleProdMessage] = useState<string | null>(null);
  const [googleProdError, setGoogleProdError] = useState<string | null>(null);

  // #461 — the ids the panels see and the requests carry: blank the instant
  // the scope is unresolved or the stamp names a different scope.
  const googleTraderId = googleTraderScopeReady
    && googleTraderSelection.scopeKey === googleTraderScopeKey
    ? googleTraderSelection.traderId
    : '';
  const googleProdTraderId = googleTraderScopeReady
    && googleProdTraderSelection.scopeKey === googleTraderScopeKey
    ? googleProdTraderSelection.traderId
    : '';

  const setGoogleTraderId = (traderId: string) => {
    setGoogleTraderSelection({ scopeKey: googleTraderScopeKey, traderId });
  };
  const setGoogleProdTraderId = (traderId: string) => {
    setGoogleProdTraderSelection({ scopeKey: googleTraderScopeKey, traderId });
  };

  // Governance keeps the OUTGOING org's members array during a scope switch;
  // the Google panels must not offer it as options while the new scope loads.
  const googleAccounts = googleTraderScopeReady ? members : EMPTY_GOOGLE_ACCOUNTS;

  useEffect(() => {
    setSelectedExportTraderIds([]);
  }, [orgQueryParam]);

  useEffect(() => {
    const exportableIds = new Set(members.map((m) => m.id));
    setSelectedExportTraderIds((prev) => {
      const next = prev.filter((id) => exportableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [members]);

  const downloadExport = (
    targetKey: string,
    endpoint: string,
    fallbackFilename: string,
    traderIds: string[] = [],
  ) => {
    // Retry re-runs with the CURRENT form settings (the export is an idempotent
    // GET and the range is captured fresh here at click time — deliberate).
    exports.run(targetKey, async () => {
      const { filename, rowCount } = await fetchCsvExport(
        buildExportUrl(endpoint, orgQueryParam, {
          fromDate: exportFromDate,
          toDate: exportToDate,
          traderIds,
        }),
        fallbackFilename,
      );
      return rowCount == null
        ? `${filename} downloaded.`
        : `${filename} downloaded (${Number(rowCount).toLocaleString()} rows).`;
    });
  };

  const handleOrgExport = () => {
    downloadExport(
      'org',
      '/api/manager/org/trade-export.csv',
      'org-trade-export.csv',
      selectedExportTraderIds,
    );
  };

  /**
   * Order audit CSV (#133). Presentation only: it calls the frozen #109 endpoint
   * and hands the bytes straight to the browser.
   *
   * Uses downloadExport — unlike Settlements below, this export DOES carry the
   * Fill/accounting date range and trader selection. #133 says to reuse them, and
   * the endpoint's parseExportRange reads an absent from/to as all history
   * (epoch → now), so a blank range widens the file instead of silently
   * filtering it. Both are honest here; on settlements a range is not.
   *
   * Not to be confused with /org/order-lifecycle-export.csv — a different data
   * plane (per-order reconciliation) whose download UI is a separate future
   * child of #156, not this issue.
   */
  const handleOrgOrderAuditExport = () => {
    downloadExport(
      'org-order-audit',
      '/api/manager/org/order-audit-export.csv',
      'org-order-audit-export.csv',
      selectedExportTraderIds,
    );
  };

  /**
   * Settlements CSV (#122). Separate from downloadExport because the settlement
   * export must NOT carry the Fill/accounting date range: it is a current
   * snapshot, and a settled_at filter drops every open position (open rows have
   * a null settled_at). The per-trader settlement route rejects from/to with a
   * 400 outright. Empty dates make buildExportUrl omit both params.
   */
  const downloadSettlementExport = (
    targetKey: string,
    endpoint: string,
    fallbackFilename: string,
  ) => {
    exports.run(targetKey, async () => {
      const { filename, rowCount } = await fetchCsvExport(
        buildExportUrl(endpoint, orgQueryParam, { fromDate: '', toDate: '' }),
        fallbackFilename,
      );
      return rowCount == null
        ? `${filename} downloaded.`
        : `${filename} downloaded (${Number(rowCount).toLocaleString()} rows).`;
    });
  };

  const handleOrgSettlementExport = () => {
    downloadSettlementExport(
      'org-settlements',
      '/api/manager/org/settlements-titled.csv',
      'org-settlements-titled.csv',
    );
  };

  const handleTraderExport = (member: OrgMember) => {
    downloadExport(
      member.id,
      `/api/manager/traders/${member.id}/trade-export.csv`,
      `trader-${member.username}-trade-export.csv`,
    );
  };

  // Keyed separately from the other per-trader exports so every button on a card
  // keeps independent pending/error state and none swallows another's click as a
  // duplicate.
  const handleTraderSettlementExport = (member: OrgMember) => {
    downloadSettlementExport(
      `${member.id}-settlements`,
      `/api/manager/traders/${member.id}/settlements-titled.csv`,
      `trader-${member.username}-settlements-titled.csv`,
    );
  };

  // Keyed separately from the other per-trader exports so every button on a card
  // keeps independent pending/error state and none swallows another's click as a
  // duplicate. Passes no traderIds: the per-trader route takes its subject from
  // the path and ignores the query.
  const handleTraderOrderAuditExport = (member: OrgMember) => {
    downloadExport(
      `${member.id}-order-audit`,
      `/api/manager/traders/${member.id}/order-audit-export.csv`,
      `trader-${member.username}-order-audit-export.csv`,
    );
  };

  const handleExportPreset = (days: number | null) => {
    const next = exportPresetRange(days);
    setExportFromDate(next.fromDate);
    setExportToDate(next.toDate);
  };

  const exportMembers = members;

  // #461 — each default effect first discards a stamp left by a previous
  // scope (so an old manual choice cannot revive when its org is re-selected
  // later), then defaults from CURRENT-scope accounts only. The key-mismatch
  // branch is deliberately separate from `!googleTraderScopeReady`: re-stamping
  // on every not-ready render would loop.
  useEffect(() => {
    if (googleTraderSelection.scopeKey !== googleTraderScopeKey) {
      setGoogleTraderSelection({ scopeKey: googleTraderScopeKey, traderId: '' });
      return;
    }
    if (!googleTraderScopeReady || googleTraderId || googleAccounts.length === 0) return;
    const prof = googleAccounts.find((member) => member.username.toLowerCase() === 'prof');
    setGoogleTraderSelection({
      scopeKey: googleTraderScopeKey,
      traderId: (prof ?? googleAccounts[0]).id,
    });
  }, [
    googleAccounts,
    googleTraderId,
    googleTraderScopeKey,
    googleTraderScopeReady,
    googleTraderSelection.scopeKey,
  ]);

  useEffect(() => {
    if (googleProdTraderSelection.scopeKey !== googleTraderScopeKey) {
      setGoogleProdTraderSelection({ scopeKey: googleTraderScopeKey, traderId: '' });
      return;
    }
    if (!googleTraderScopeReady || googleProdTraderId || googleAccounts.length === 0) return;
    const prof = googleAccounts.find((member) => member.username.toLowerCase() === 'prof');
    setGoogleProdTraderSelection({
      scopeKey: googleTraderScopeKey,
      traderId: (prof ?? googleAccounts[0]).id,
    });
  }, [
    googleAccounts,
    googleProdTraderId,
    googleProdTraderSelection.scopeKey,
    googleTraderScopeKey,
    googleTraderScopeReady,
  ]);

  useEffect(() => {
    setGoogleProdPreview(null);
    setGoogleProdConfirmation('');
  }, [
    googleProdSpreadsheetId,
    googleProdSourceSheetTitle,
    googleProdTargetSheetTitle,
    googleProdTraderId,
    googleProdFromDate,
    googleProdToDate,
    googleProdVenue,
    googleProdAllowPolymarket,
    googleProdSourceLabel,
    googleProdBettorLabel,
    googleProdBookLabel,
    orgQueryParam,
  ]);

  const handleGoogleAllTradersChange = (next: boolean) => {
    setGoogleAllTraders(next);
    setGoogleTargetSheetTitle((current) => (
      isDefaultGoogleTestSheetTitle(current)
        ? defaultGoogleTestSheetTitle(next ? 'Org Trades' : 'Prof Trades')
        : current
    ));
  };

  const handleGoogleSheetLoad = async () => {
    setGoogleLoading(true);
    setGoogleError(null);
    setGoogleMessage(null);
    try {
      const res = await apiFetch(`/api/manager/org/google-sheets/moving-test-load${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          spreadsheetId: googleSpreadsheetId,
          targetSheetTitle: googleTargetSheetTitle,
          allTraders: googleAllTraders,
          traderId: googleAllTraders ? undefined : googleTraderId,
          from: googleFromDate,
          to: googleToDate,
          venue: 'kalshi',
          sourceLabel: googleAllTraders ? undefined : googleSourceLabel,
          bettorLabel: googleAllTraders ? undefined : googleBettorLabel,
          bookLabel: googleBookLabel,
          accessToken: googleAccessToken,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error ?? 'Google Sheets load failed');
      }
      setGoogleMessage(
        `${data.targetSheetTitle} created (${Number(data.rowCount ?? 0).toLocaleString()} rows).`,
      );
      setGoogleAccessToken('');
      setGoogleTargetSheetTitle(defaultGoogleTestSheetTitle(googleAllTraders ? 'Org Trades' : 'Prof Trades'));
    } catch (err) {
      setGoogleError(err instanceof Error ? err.message : 'Google Sheets load failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const googleProductionBody = () => ({
    spreadsheetId: googleProdSpreadsheetId,
    sourceSheetTitle: googleProdSourceSheetTitle,
    targetSheetTitle: googleProdTargetSheetTitle,
    traderId: googleProdTraderId,
    from: googleProdFromDate,
    to: googleProdToDate,
    venue: googleProdVenue,
    allowPolymarket: googleProdAllowPolymarket,
    sourceLabel: googleProdSourceLabel,
    bettorLabel: googleProdBettorLabel,
    bookLabel: googleProdBookLabel,
    accessToken: googleProdAccessToken,
  });

  const handleGoogleProductionPreview = async () => {
    setGoogleProdLoading('preview');
    setGoogleProdMessage(null);
    setGoogleProdError(null);
    try {
      const res = await apiFetch(`/api/manager/org/google-sheets/moving-production-preview${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(googleProductionBody()),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error ?? 'Google Sheets preview failed');
      }
      setGoogleProdPreview(data as GoogleProductionPreview);
      setGoogleProdMessage(
        `Preview ready: ${Number(data.counts?.inserts ?? 0).toLocaleString()} insert, `
          + `${Number(data.counts?.updates ?? 0).toLocaleString()} update, `
          + `${Number(data.counts?.seen ?? 0).toLocaleString()} seen.`,
      );
    } catch (err) {
      setGoogleProdPreview(null);
      setGoogleProdError(err instanceof Error ? err.message : 'Google Sheets preview failed');
    } finally {
      setGoogleProdLoading(null);
    }
  };

  const handleGoogleProductionApply = async () => {
    if (!googleProdPreview) return;
    setGoogleProdLoading('apply');
    setGoogleProdMessage(null);
    setGoogleProdError(null);
    try {
      const res = await apiFetch(`/api/manager/org/google-sheets/moving-production-upsert${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...googleProductionBody(),
          previewFingerprint: googleProdPreview.fingerprint,
          confirmation: googleProdConfirmation,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error ?? 'Google Sheets upsert failed');
      }
      setGoogleProdPreview(data as GoogleProductionPreview);
      setGoogleProdMessage(
        `Applied ${Number(data.writtenRows ?? 0).toLocaleString()} row touch`
          + `${Number(data.writtenRows ?? 0) === 1 ? '' : 'es'} to ${data.targetSheetTitle}.`,
      );
      setGoogleProdConfirmation('');
      setGoogleProdAccessToken('');
    } catch (err) {
      setGoogleProdError(err instanceof Error ? err.message : 'Google Sheets upsert failed');
    } finally {
      setGoogleProdLoading(null);
    }
  };

  /**
   * The nine opaque export props each member card receives. #342 left this as a
   * prop on MembersPanel precisely so #343 could move the implementation here
   * without governance ever learning what an export is.
   *
   * Each button is keyed separately so one card's three exports keep
   * independent pending/error state and none swallows another's click as a
   * duplicate (#126).
   */
  const exportSlotsFor = (member: OrgMember): MemberExportSlots => ({
    onExport: () => handleTraderExport(member),
    exporting: exports.isPending(member.id),
    exportError: exports.entry(member.id)?.status === 'error' ? exports.entry(member.id)?.error ?? null : null,
    onSettlementExport: () => handleTraderSettlementExport(member),
    settlementExporting: exports.isPending(`${member.id}-settlements`),
    settlementExportError:
      exports.entry(`${member.id}-settlements`)?.status === 'error'
        ? exports.entry(`${member.id}-settlements`)?.error ?? null
        : null,
    onOrderAuditExport: () => handleTraderOrderAuditExport(member),
    orderAuditExporting: exports.isPending(`${member.id}-order-audit`),
    orderAuditExportError:
      exports.entry(`${member.id}-order-audit`)?.status === 'error'
        ? exports.entry(`${member.id}-order-audit`)?.error ?? null
        : null,
  });

  return {
    exportFromDate,
    setExportFromDate,
    exportToDate,
    setExportToDate,
    selectedExportTraderIds,
    setSelectedExportTraderIds,
    googleSpreadsheetId,
    setGoogleSpreadsheetId,
    googleTargetSheetTitle,
    setGoogleTargetSheetTitle,
    googleTraderId,
    setGoogleTraderId,
    googleAllTraders,
    setGoogleAllTraders,
    googleFromDate,
    setGoogleFromDate,
    googleToDate,
    setGoogleToDate,
    googleAccessToken,
    setGoogleAccessToken,
    googleSourceLabel,
    setGoogleSourceLabel,
    googleBettorLabel,
    setGoogleBettorLabel,
    googleBookLabel,
    setGoogleBookLabel,
    googleLoading,
    setGoogleLoading,
    googleMessage,
    setGoogleMessage,
    googleError,
    setGoogleError,
    googleProdSpreadsheetId,
    setGoogleProdSpreadsheetId,
    googleProdSourceSheetTitle,
    setGoogleProdSourceSheetTitle,
    googleProdTargetSheetTitle,
    setGoogleProdTargetSheetTitle,
    googleProdTraderId,
    setGoogleProdTraderId,
    googleProdFromDate,
    setGoogleProdFromDate,
    googleProdToDate,
    setGoogleProdToDate,
    googleProdVenue,
    setGoogleProdVenue,
    googleProdAllowPolymarket,
    setGoogleProdAllowPolymarket,
    googleProdAccessToken,
    setGoogleProdAccessToken,
    googleProdSourceLabel,
    setGoogleProdSourceLabel,
    googleProdBettorLabel,
    setGoogleProdBettorLabel,
    googleProdBookLabel,
    setGoogleProdBookLabel,
    googleProdPreview,
    setGoogleProdPreview,
    googleProdConfirmation,
    setGoogleProdConfirmation,
    googleProdLoading,
    setGoogleProdLoading,
    googleProdMessage,
    setGoogleProdMessage,
    googleProdError,
    setGoogleProdError,
    exports,
    exportMembers,
    googleAccounts,
    // #486 — the panels' action buttons gate on the SAME readiness the ids and
    // the options derive from, rather than relying on a blank trader id to do
    // it for them. Re-exported unchanged so there is one definition, not two.
    googleTraderScopeReady,
    handleOrgExport,
    handleOrgSettlementExport,
    handleOrgOrderAuditExport,
    handleExportPreset,
    handleGoogleAllTradersChange,
    handleGoogleSheetLoad,
    handleGoogleProductionPreview,
    handleGoogleProductionApply,
    exportSlotsFor,
  };
}
